import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  LayoutDashboard,
  Key,
  Users,
  Package,
  Building2,
  UserPlus,
  FileText,
  Cpu,
  ScrollText,
  Shield,
  CheckCircle2,
  PackageCheck,
  BarChart3,
  Activity,
  Bell,
  Webhook,
  CreditCard,
  Settings,
  ShieldCheck,
  Download,
  Book,
} from 'lucide-react';

type UserRole = 'SUPER_ADMIN' | 'OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER';

interface NavItem {
  nameKey: string;
  path: string;
  icon: any;
  categoryKey: string;
  allowedRoles?: UserRole[];
}

const navItems: NavItem[] = [
  { nameKey: 'navigation.dashboard', path: '/', icon: LayoutDashboard, categoryKey: 'commandPalette.categories.overview' },
  { nameKey: 'navigation.downloads', path: '/downloads', icon: Download, categoryKey: 'commandPalette.categories.overview' },
  { nameKey: 'navigation.sdkDocs', path: '/sdk-docs', icon: Book, categoryKey: 'commandPalette.categories.overview' },
  { nameKey: 'navigation.licenses', path: '/licenses', icon: Key, categoryKey: 'commandPalette.categories.licenseManagement' },
  { nameKey: 'Policies', path: '/policies', icon: ScrollText, categoryKey: 'commandPalette.categories.licenseManagement', allowedRoles: ['OWNER', 'ADMIN'] },
  { nameKey: 'navigation.activations', path: '/activations', icon: CheckCircle2, categoryKey: 'commandPalette.categories.licenseManagement', allowedRoles: ['OWNER', 'ADMIN', 'MEMBER'] },
  { nameKey: 'navigation.verificationPackages', path: '/verification', icon: PackageCheck, categoryKey: 'commandPalette.categories.licenseManagement', allowedRoles: ['OWNER', 'ADMIN', 'MEMBER'] },
  { nameKey: 'navigation.users', path: '/users', icon: Users, categoryKey: 'commandPalette.categories.usersAccess', allowedRoles: ['OWNER', 'ADMIN'] },
  { nameKey: 'navigation.rolesAndPermissions', path: '/roles', icon: Shield, categoryKey: 'commandPalette.categories.usersAccess', allowedRoles: ['OWNER', 'ADMIN'] },
  { nameKey: 'navigation.apiKeys', path: '/api-keys', icon: Key, categoryKey: 'commandPalette.categories.usersAccess', allowedRoles: ['OWNER', 'ADMIN'] },
  { nameKey: 'navigation.products', path: '/products', icon: Package, categoryKey: 'commandPalette.categories.catalog' },
  { nameKey: 'navigation.customers', path: '/customers', icon: Building2, categoryKey: 'commandPalette.categories.catalog', allowedRoles: ['OWNER', 'ADMIN', 'MEMBER'] },
  { nameKey: 'navigation.partners', path: '/partners', icon: UserPlus, categoryKey: 'commandPalette.categories.partners', allowedRoles: ['OWNER', 'ADMIN'] },
  { nameKey: 'navigation.purchaseOrders', path: '/purchase-orders', icon: FileText, categoryKey: 'commandPalette.categories.partners', allowedRoles: ['OWNER', 'ADMIN'] },
  { nameKey: 'navigation.organizations', path: '/organizations', icon: Building2, categoryKey: 'commandPalette.categories.configuration', allowedRoles: ['OWNER', 'ADMIN'] },
  { nameKey: 'navigation.webhooks', path: '/webhooks', icon: Webhook, categoryKey: 'commandPalette.categories.configuration', allowedRoles: ['OWNER', 'ADMIN'] },
  { nameKey: 'navigation.billing', path: '/billing', icon: CreditCard, categoryKey: 'commandPalette.categories.billing', allowedRoles: ['OWNER', 'ADMIN'] },
  { nameKey: 'navigation.analytics', path: '/analytics', icon: BarChart3, categoryKey: 'commandPalette.categories.insights', allowedRoles: ['OWNER', 'ADMIN', 'MEMBER'] },
  { nameKey: 'navigation.systemLogs', path: '/system-logs', icon: ScrollText, categoryKey: 'commandPalette.categories.insights', allowedRoles: ['OWNER', 'ADMIN'] },
  { nameKey: 'navigation.activityLogs', path: '/activity-logs', icon: Activity, categoryKey: 'commandPalette.categories.insights', allowedRoles: ['OWNER', 'ADMIN', 'MEMBER'] },
  { nameKey: 'navigation.systemSignatures', path: '/system-signatures', icon: Cpu, categoryKey: 'commandPalette.categories.security', allowedRoles: ['OWNER', 'ADMIN'] },
  { nameKey: 'navigation.mfa', path: '/mfa', icon: ShieldCheck, categoryKey: 'commandPalette.categories.security' },
  { nameKey: 'navigation.settings', path: '/settings', icon: Settings, categoryKey: 'commandPalette.categories.account' },
  { nameKey: 'navigation.notifications', path: '/notifications', icon: Bell, categoryKey: 'commandPalette.categories.account' },
];

interface CommandPaletteProps {
  user?: any;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CommandPalette({ user, open: controlledOpen, onOpenChange }: CommandPaletteProps) {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const navigate = useNavigate();

  // Support both controlled and uncontrolled usage
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? (onOpenChange ?? (() => {})) : setInternalOpen;

  // Helper function to check if user has access to a nav item
  const hasAccess = useCallback((item: NavItem): boolean => {
    if (!item.allowedRoles || item.allowedRoles.length === 0) {
      return true;
    }
    if (!user || !user.role) {
      return false;
    }
    if (user.role === 'SUPER_ADMIN') {
      return true;
    }
    return item.allowedRoles.includes(user.role);
  }, [user]);

  // Filter items based on user access
  const accessibleItems = navItems.filter(hasAccess);

  // Group items by category
  const groupedItems = accessibleItems.reduce((acc, item) => {
    const category = t(item.categoryKey);
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {} as Record<string, NavItem[]>);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(!open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [open, setOpen]);

  const handleSelect = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="overflow-hidden p-0 shadow-lg max-w-2xl">
        <Command className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          <CommandInput placeholder={t('commandPalette.placeholder')} />
          <CommandList className="max-h-[400px]">
            <CommandEmpty>{t('commandPalette.noResults')}</CommandEmpty>
            {Object.entries(groupedItems).map(([category, items]) => (
              <CommandGroup key={category} heading={category}>
                {items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <CommandItem
                      key={item.path}
                      value={t(item.nameKey)}
                      onSelect={() => handleSelect(item.path)}
                      className="cursor-pointer"
                    >
                      <Icon className="mr-2 h-4 w-4" />
                      <span>{t(item.nameKey)}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
